/**
 * Donut charts — renders an SVG donut inside each .wp-block-donut-table's
 * [data-donut] container from the values in its sibling core/table.
 *
 * Contract: each tbody row = one segment; first cell is the label, last
 * cell holds the numeric value ("$5,041,151", "48%", …). Rows whose last
 * cell isn't numeric, or whose label reads TOTAL, are skipped. A color
 * swatch is injected into each charted row's first cell so the table
 * doubles as the legend.
 *
 * Segments are drawn as annular sectors (filled paths), not as dashes on a
 * stroked circle. The dash approach breaks down once a segment's arc is
 * shorter than the stroke width — at this radius that is anything under
 * ~8%, which malformed the 7.5% and 6% slices. Paths are exact at any size.
 *
 * The donut sweeps in when scrolled into view, matching the count-up on the
 * big-stat block; reduced motion draws it complete.
 */

const SIZE = 42;
const CENTER = SIZE / 2;
const R_OUTER = 19.92;
const R_INNER = 11.92;
const START = -Math.PI / 2; // 12 o'clock
const FULL = Math.PI * 2;
const DURATION = 1400;

function chartColors(): string[] {
  const styles = getComputedStyle(document.documentElement);
  return [1, 2, 3, 4, 5, 6, 7, 8]
    .map((i) => styles.getPropertyValue(`--chart-${i}`).trim())
    .filter(Boolean);
}

function parseValue(text: string): number {
  const value = parseFloat(text.replace(/[^0-9.]/g, ''));
  return Number.isNaN(value) ? NaN : value;
}

function isTotalRow(row: HTMLTableRowElement): boolean {
  return /total/i.test(row.cells[0]?.textContent ?? '');
}

function point(radius: number, angle: number): string {
  return `${(CENTER + radius * Math.cos(angle)).toFixed(3)} ${(CENTER + radius * Math.sin(angle)).toFixed(3)}`;
}

/** Annular sector from a0 to a1, outer edge clockwise then inner edge back. */
function wedge(a0: number, a1: number): string {
  const span = Math.min(a1 - a0, FULL - 1e-4);
  if (span <= 1e-6) return '';
  const end = a0 + span;
  const large = span > Math.PI ? 1 : 0;
  return (
    `M${point(R_OUTER, a0)}A${R_OUTER} ${R_OUTER} 0 ${large} 1 ${point(R_OUTER, end)}` +
    `L${point(R_INNER, end)}A${R_INNER} ${R_INNER} 0 ${large} 0 ${point(R_INNER, a0)}Z`
  );
}

export default function donuts() {
  const colors = chartColors();
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  document.querySelectorAll<HTMLElement>('.wp-block-donut-table').forEach((block) => {
    const container = block.querySelector<HTMLElement>('[data-donut]');
    const rows = block.querySelectorAll<HTMLTableRowElement>('tbody tr');
    if (!container || !rows.length) return;

    const segments: { row: HTMLTableRowElement; value: number }[] = [];
    rows.forEach((row) => {
      const last = row.cells[row.cells.length - 1];
      const value = parseValue(last?.textContent ?? '');
      if (!Number.isNaN(value) && value > 0 && !isTotalRow(row)) {
        segments.push({ row, value });
      }
    });
    if (!segments.length) return;

    const total = segments.reduce((sum, s) => sum + s.value, 0);

    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', `0 0 ${SIZE} ${SIZE}`);
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');

    let angle = START;
    const arcs = segments.map((segment, i) => {
      const color = colors[i % colors.length];
      const span = (segment.value / total) * FULL;
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('fill', color);
      svg.append(path);

      const swatch = document.createElement('span');
      swatch.className = 'donut-swatch';
      swatch.style.backgroundColor = color;
      swatch.setAttribute('aria-hidden', 'true');
      segment.row.cells[0]?.prepend(swatch);

      const arc = { path, from: angle, span };
      angle += span;
      return arc;
    });

    // one sweep around the whole donut; each segment fills as it is reached
    const draw = (progress: number) => {
      const swept = START + progress * FULL;
      arcs.forEach(({ path, from, span }) => {
        const drawn = Math.min(Math.max(swept - from, 0), span);
        path.setAttribute('d', wedge(from, from + drawn));
      });
    };

    container.replaceChildren(svg);

    if (reduced) {
      draw(1);
      return;
    }

    draw(0);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          const start = performance.now();
          const tick = (now: number) => {
            const progress = Math.min((now - start) / DURATION, 1);
            draw(1 - Math.pow(1 - progress, 3));
            if (progress < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        });
      },
      { threshold: 0.3 }
    );
    observer.observe(container);
  });
}
